import { DateField } from '@mui/x-date-pickers/DateField';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import Popover from '@mui/material/Popover';
import CalendarTodayRoundedIcon from '@mui/icons-material/CalendarTodayRounded';
import { DateCalendarBase } from 'components/date-calendar/DateCalendarBase';
import type { EditableFieldSharedProps } from './types';
import { useCustomFieldDateInput } from './useCustomFieldDateInput';

export function CustomFieldDateInput({ field, mutate, dispatch, currentValue, fieldTypeOption, t }: EditableFieldSharedProps) {
  const { localValue, isOpen, setIsOpen, iconButtonRef, handleChange, handleCalendarSelect, handleError, handleBlur } =
    useCustomFieldDateInput({ field, mutate, dispatch, currentValue });

  return (
    <>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <DateField
          label={field.friendlyName}
          value={localValue}
          onChange={handleChange}
          onError={handleError}
          format="DD/MM/YYYY"
          slotProps={{
            textField: {
              size: 'small',
              fullWidth: true,
              variant: 'outlined',
              placeholder: field.placeholder || t(fieldTypeOption.defaultPlaceholderKey),
              InputLabelProps: { disableAnimation: true, shrink: true },
              InputProps: {
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      ref={iconButtonRef}
                      size="small"
                      edge="end"
                      onClick={() => setIsOpen(true)}
                      aria-label={t('editor.variables.custom_field_date_picker_open', 'Open date picker')}
                    >
                      <CalendarTodayRoundedIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              },
              inputProps: {
                'data-dd-action-name': 'Custom Field Date Input',
              },
              onBlur: handleBlur,
            },
          }}
        />
      </LocalizationProvider>
      <Popover
        open={isOpen}
        anchorEl={iconButtonRef.current}
        onClose={() => setIsOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { borderRadius: 2, mt: 1 } } }}
      >
        <DateCalendarBase value={localValue} onChange={handleCalendarSelect} />
      </Popover>
    </>
  );
}
